import React, { useEffect, useState, useCallback } from "react";
import api, { rupees, formatApiError } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { PageHead, Loading, Pill, BrandDot, Empty } from "@/components/common";
import { toast } from "sonner";
import { AddSkuModal } from "@/components/AddSkuModal";
import { Search, X, AlertTriangle, RefreshCw, Plus } from "lucide-react";

export default function Products() {
  const { selectedBrand, brands } = useApp();
  const [data, setData] = useState(null);
  const [facets, setFacets] = useState({ categories: [] });
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("");
  const [status, setStatus] = useState("");
  const [page, setPage] = useState(1);
  const [edit, setEdit] = useState(null);
  const [batches, setBatches] = useState([]);
  const [showAdd, setShowAdd] = useState(false);

  const load = useCallback(() => {
    const p = new URLSearchParams();
    if (selectedBrand !== "ALL") p.set("brand", selectedBrand);
    if (search) p.set("search", search);
    if (category) p.set("category", category);
    if (status) p.set("status", status);
    p.set("page", page); p.set("page_size", 20);
    api.get(`/skus?${p}`).then((r) => setData(r.data));
  }, [selectedBrand, search, category, status, page]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/skus/facets").then((r) => setFacets(r.data)); api.get("/import-batches").then((r) => setBatches(r.data)); }, []);

  const reimport = async () => {
    const { data } = await api.post("/import/reimport");
    toast.success(data.message);
    api.get("/import-batches").then((r) => setBatches(r.data));
    load();
  };

  const saveEdit = async () => {
    try {
      const body = {};
      if (edit.reward_points !== "" && edit.reward_points != null) body.reward_points = parseInt(edit.reward_points);
      if (edit._category) body.category = edit._category;
      if (edit._approve) body.approve_code = true;
      if (edit._activate) body.status = "active";
      await api.patch(`/skus/${edit.id}`, body);
      toast.success("SKU updated");
      setEdit(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const batch = batches[0];

  return (
    <div>
      <PageHead overline="SKU Master" title={`Products / SKUs${data ? ` (${data.total})` : ""}`}
        desc="147 real workbook records imported in Draft / Needs-configuration state. Reward points are NULL and must be approved before QR activation."
        actions={<>
          <button data-testid="add-sku-btn" onClick={() => setShowAdd(true)} className="flex items-center gap-2 text-sm font-semibold text-white tnp-gradient px-3 py-2 rounded-lg"><Plus size={14} /> Add SKU</button>
          <button data-testid="reimport-btn" onClick={reimport} className="flex items-center gap-2 text-sm font-semibold text-violet-700 bg-violet-50 border border-violet-100 px-3 py-2 rounded-lg hover:bg-violet-100"><RefreshCw size={14} /> Idempotent Re-import</button>
        </>} />

      {batch && (
        <div className="mb-5 grid grid-cols-2 md:grid-cols-5 gap-3 text-sm">
          {[["Workbook import rows", batch.total_rows], ["Points pending", batch.points_pending], ["Codes missing", batch.codes_missing], ["Categories to review", batch.categories_needing_review], ["Created", batch.created]].map(([l, v]) => (
            <div key={l} className="bg-white border border-slate-200 rounded-lg px-3 py-2"><div className="text-[11px] text-slate-400">{l}</div><div className="font-bold text-slate-800">{v}</div></div>
          ))}
        </div>
      )}

      <div className="flex flex-wrap gap-3 mb-4">
        <div className="relative flex-1 min-w-[220px]">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input data-testid="product-search-input" value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} placeholder="Search name or code…"
            className="w-full pl-9 pr-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-violet-200" />
        </div>
        <select data-testid="product-category-filter" value={category} onChange={(e) => { setCategory(e.target.value); setPage(1); }} className="px-3 py-2 rounded-lg border border-slate-200 text-sm">
          <option value="">All categories</option>
          {facets.categories.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <select value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); }} className="px-3 py-2 rounded-lg border border-slate-200 text-sm">
          <option value="">All status</option><option value="draft">Draft</option><option value="active">Active</option>
        </select>
      </div>

      {!data ? <Loading /> : data.items.length === 0 ? <Empty /> : (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wider">
              <tr><th className="text-left px-4 py-3">Code</th><th className="text-left px-4 py-3">Name</th><th className="text-left px-4 py-3">Brand</th><th className="text-left px-4 py-3">Category</th><th className="text-right px-4 py-3">MRP</th><th className="text-right px-4 py-3">Points</th><th className="text-left px-4 py-3">Status</th><th></th></tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {data.items.map((s) => (
                <tr key={s.id} data-testid={`sku-row-${s.id}`} className="hover:bg-slate-50">
                  <td className="px-4 py-3"><span className="font-mono text-xs">{s.display_code}</span>{s.code_status === "proposed_awaiting_approval" && <Pill status="proposed_awaiting_approval">proposed</Pill>}</td>
                  <td className="px-4 py-3 font-medium text-slate-800 max-w-[260px] truncate">{s.name}</td>
                  <td className="px-4 py-3"><BrandDot id={s.brand_id} />{s.brand_id}</td>
                  <td className="px-4 py-3">{s.category || <span className="text-amber-600 inline-flex items-center gap-1"><AlertTriangle size={12} /> needs review</span>}</td>
                  <td className="px-4 py-3 text-right text-slate-500">{s.mrp_paise ? `₹${(s.mrp_paise / 100).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : "—"}</td>
                  <td className="px-4 py-3 text-right">{s.reward_points == null ? <span className="text-amber-600 font-semibold">NULL</span> : <span className="font-semibold">{s.reward_points}</span>}</td>
                  <td className="px-4 py-3"><Pill status={s.status}>{s.status}</Pill></td>
                  <td className="px-4 py-3 text-right"><button data-testid={`sku-edit-${s.id}`} onClick={() => setEdit({ ...s, _category: "", _approve: false, _activate: false })} className="text-violet-600 text-xs font-semibold hover:underline">Configure</button></td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="flex items-center justify-between px-4 py-3 border-t border-slate-100 text-sm text-slate-500">
            <span>Page {data.page} · {data.total} SKUs</span>
            <div className="flex gap-2">
              <button disabled={page <= 1} onClick={() => setPage(page - 1)} className="px-3 py-1 rounded border border-slate-200 disabled:opacity-40">Prev</button>
              <button disabled={page * 20 >= data.total} onClick={() => setPage(page + 1)} className="px-3 py-1 rounded border border-slate-200 disabled:opacity-40">Next</button>
            </div>
          </div>
        </div>
      )}

      {edit && (
        <div className="fixed inset-0 bg-black/40 z-50 flex justify-end" onClick={() => setEdit(null)}>
          <div className="w-full max-w-md bg-white h-full p-6 overflow-y-auto fade-in" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between mb-4">
              <div><div className="text-xs text-slate-400 font-mono">{edit.display_code}</div><h3 className="text-lg font-bold font-head">{edit.name}</h3></div>
              <button onClick={() => setEdit(null)}><X size={18} className="text-slate-400" /></button>
            </div>
            <div className="space-y-4">
              <div className="text-sm bg-slate-50 rounded-lg p-3 space-y-1">
                <div className="flex justify-between"><span className="text-slate-500">Raw name</span><span className="font-mono text-xs text-slate-600 text-right max-w-[200px]">"{edit.name_raw}"</span></div>
                <div className="flex justify-between"><span className="text-slate-500">MRP (reference)</span><span>{edit.mrp_paise ? rupees(edit.mrp_paise) : "—"}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">Source sheet / row</span><span>{edit.sheet} #{edit.source_row}</span></div>
              </div>
              <div>
                <label className="text-sm font-semibold text-slate-700">Approved reward points</label>
                <p className="text-xs text-slate-400 mb-1">Never inferred from MRP. Required before activation.</p>
                <input data-testid="sku-points-input" type="number" value={edit.reward_points ?? ""} onChange={(e) => setEdit({ ...edit, reward_points: e.target.value })}
                  className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm" placeholder="e.g. 20" />
              </div>
              {edit.category_needs_review && (
                <div>
                  <label className="text-sm font-semibold text-slate-700">Category (needs review)</label>
                  <input value={edit._category} onChange={(e) => setEdit({ ...edit, _category: e.target.value })} className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm" placeholder="Assign category" />
                </div>
              )}
              {edit.code_status === "proposed_awaiting_approval" && (
                <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={edit._approve} onChange={(e) => setEdit({ ...edit, _approve: e.target.checked })} /> Approve proposed code <span className="font-mono">{edit.display_code}</span></label>
              )}
              <label className="flex items-center gap-2 text-sm"><input data-testid="sku-activate-check" type="checkbox" checked={edit._activate} onChange={(e) => setEdit({ ...edit, _activate: e.target.checked })} /> Activate SKU (requires reward points)</label>
              <button data-testid="sku-save-btn" onClick={saveEdit} className="w-full tnp-gradient text-white font-semibold py-2.5 rounded-lg">Save configuration</button>
            </div>
          </div>
        </div>
      )}

      {showAdd && <AddSkuModal brands={brands} initialBrand={selectedBrand !== "ALL" ? selectedBrand : brands[0]?.id} onClose={() => setShowAdd(false)} onCreated={() => { setShowAdd(false); load(); }} />}
    </div>
  );
}
